import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "./auth-client";
import { Sentry } from "./sentry";

export type ProfileResponse = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  emailVerified: boolean;
  image: string | null;
  defaultOrganizationId: string | null;
};

export type OrganizationSummary = {
  id: string;
  name: string;
  slug: string;
  capability: "full_marketplace" | "seller_only";
  role: string;
  isDefault: boolean;
};

type PlatformAuthContextValue = {
  sessionData: ReturnType<typeof useSession>["data"];
  isPending: boolean;
  bootstrapLoading: boolean;
  profileSetupGateReady: boolean;
  profile: ProfileResponse | null;
  passkeyCount: number;
  organizations: OrganizationSummary[];
  defaultOrganization: OrganizationSummary | null;
  setupIncomplete: boolean;
  profileIncomplete: boolean;
  needsProfileSetup: boolean;
  deferProfileSetup: () => void;
  clearDeferredProfileSetup: () => void;
  refresh: () => Promise<void>;
};

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

function deferredProfileSetupKey(userId: string) {
  return `platform-profile-setup-deferred:${userId}`;
}

export function platformLastOrganizationKey(userId: string) {
  return `platform-last-organization:${userId}`;
}

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const { data: sessionData, isPending } = useSession();
  const previousUserIdRef = useRef<string | null>(null);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [passkeyCount, setPasskeyCount] = useState(0);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [profileSetupDeferred, setProfileSetupDeferred] = useState(false);
  const [profileSetupGateReady, setProfileSetupGateReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!sessionData?.user) {
      setProfile(null);
      setPasskeyCount(0);
      setOrganizations([]);
      return;
    }

    setBootstrapLoading(true);

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const fetchBootstrap = () =>
      Promise.all([
        fetch(`${api}/api/users/profile`, { credentials: "include" }),
        fetch(`${api}/api/users/passkeys`, { credentials: "include" }),
        fetch(`${api}/api/organizations`, { credentials: "include" }),
      ]);

    try {
      let responses: [Response, Response, Response];
      try {
        responses = await fetchBootstrap();
      } catch {
        // Transient network failure during auth bootstrap (flaky mobile
        // connection, tab backgrounded mid-fetch). Retry once before giving up.
        await new Promise((resolve) => setTimeout(resolve, 500));
        responses = await fetchBootstrap();
      }
      const [profileRes, passkeyRes, organizationsRes] = responses;

      if (profileRes.ok) {
        setProfile((await profileRes.json()) as ProfileResponse);
      } else {
        setProfile(null);
      }

      if (passkeyRes.ok) {
        const data = (await passkeyRes.json()) as { count: number };
        setPasskeyCount(data.count ?? 0);
      } else {
        setPasskeyCount(0);
      }

      if (organizationsRes.ok) {
        setOrganizations((await organizationsRes.json()) as OrganizationSummary[]);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      // Network still unreachable after a retry. Keep any existing state and
      // report as handled so this doesn't surface as an unhandled rejection.
      Sentry.captureException(error, {
        tags: { area: "platform-auth-bootstrap" },
      });
    } finally {
      setBootstrapLoading(false);
    }
  }, [sessionData?.user]);

  useEffect(() => {
    if (isPending) {
      return;
    }

    if (sessionData?.user) {
      void refresh();
    } else {
      if (typeof window !== "undefined") {
        const previousUserId = previousUserIdRef.current;
        if (previousUserId) {
          window.sessionStorage.removeItem(deferredProfileSetupKey(previousUserId));
        }
      }
      setProfile(null);
      setPasskeyCount(0);
      setOrganizations([]);
      setProfileSetupDeferred(false);
      setProfileSetupGateReady(false);
    }
  }, [isPending, refresh, sessionData?.user]);

  useEffect(() => {
    previousUserIdRef.current = sessionData?.user?.id ?? null;
  }, [sessionData?.user?.id]);

  useEffect(() => {
    const userId = sessionData?.user?.id;
    if (!userId) {
      setProfileSetupDeferred(false);
      setProfileSetupGateReady(false);
      return;
    }

    setProfileSetupDeferred(
      typeof window !== "undefined" &&
        window.sessionStorage.getItem(deferredProfileSetupKey(userId)) === "true",
    );
    setProfileSetupGateReady(true);
  }, [sessionData?.user?.id]);

  const setupIncomplete =
    !!profile &&
    (!profile.firstName || !profile.phoneNumber || !profile.emailVerified || passkeyCount === 0);

  const profileIncomplete =
    !!profile &&
    !profile.firstName;
  const needsProfileSetup = setupIncomplete && !profileSetupDeferred;

  const deferProfileSetup = useCallback(() => {
    const userId = sessionData?.user?.id;
    if (!userId || typeof window === "undefined") return;
    window.sessionStorage.setItem(deferredProfileSetupKey(userId), "true");
    setProfileSetupDeferred(true);
  }, [sessionData?.user?.id]);

  const clearDeferredProfileSetup = useCallback(() => {
    const userId = sessionData?.user?.id;
    if (!userId || typeof window === "undefined") return;
    window.sessionStorage.removeItem(deferredProfileSetupKey(userId));
    setProfileSetupDeferred(false);
  }, [sessionData?.user?.id]);

  useEffect(() => {
    if (!setupIncomplete) {
      clearDeferredProfileSetup();
    }
  }, [clearDeferredProfileSetup, setupIncomplete]);

  const defaultOrganization =
    organizations.find((organization) => organization.isDefault) ?? organizations[0] ?? null;

  const value = useMemo<PlatformAuthContextValue>(
    () => ({
      sessionData,
      isPending,
      bootstrapLoading,
      profileSetupGateReady,
      profile,
      passkeyCount,
      organizations,
      defaultOrganization,
      setupIncomplete,
      profileIncomplete,
      needsProfileSetup,
      deferProfileSetup,
      clearDeferredProfileSetup,
      refresh,
    }),
    [
      bootstrapLoading,
      clearDeferredProfileSetup,
      deferProfileSetup,
      defaultOrganization,
      isPending,
      needsProfileSetup,
      organizations,
      passkeyCount,
      profileSetupGateReady,
      setupIncomplete,
      profileIncomplete,
      profile,
      refresh,
      sessionData,
    ],
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const context = useContext(PlatformAuthContext);

  if (!context) {
    throw new Error("usePlatformAuth must be used within PlatformAuthProvider");
  }

  return context;
}
